import * as THREE from 'three';
import type { PlanetDef, PlanetSampler, TerrainSample } from '@hyperlight/shared';
import { groundBeach, groundFertility, groundSnow, hasLivingGround } from './groundSurface';
import type { FootstepSurface } from '../audio/footsteps';

/** Same physical transitions as the ground shader; roads are painted last. */
export function terrainFootstepSurface(def: PlanetDef, sample: TerrainSample, amplitude: number, slope: number): FootstepSurface {
  const height = sample.h * amplitude;
  if (sample.road < .25) return 'gravel';
  if (slope < .75) return 'stone';
  if (groundSnow(sample.temp, height, def.climate.snowline * amplitude) > .5) return 'snow';
  if (def.seaRadius != null && groundBeach(height - (def.seaRadius - def.radius), slope) > .5) return 'sand';
  if (def.archetype === 'desert') return 'sand';
  if (def.archetype === 'ice') return 'snow';
  if (hasLivingGround(def)) return groundFertility(sample.temp, sample.moist) > .45 && sample.town < .5 ? 'grass' : 'dirt';
  return sample.mountain > .4 ? 'stone' : 'gravel';
}

/** Short radial probe of explicitly tagged architecture only. No terrain,
 * foliage, water, characters or scene-wide triangle raycast. Called on steps,
 * not frames; per-instance tags preserve mixed floors in a single draw call. */
export class FootstepSurfaceProbe {
  private ray = new THREE.Raycaster();
  private local = new THREE.Vector3();
  private up = new THREE.Vector3();
  private normal = new THREE.Vector3();
  private normalMatrix = new THREE.Matrix3();
  private instance = new THREE.Matrix4();
  private matrix = new THREE.Matrix4();
  private hits: THREE.Intersection[] = [];
  private terrainNormal = [0, 0, 0];
  private sample: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };

  flooring(root: THREE.Object3D, feet: THREE.Vector3, up: THREE.Vector3): FootstepSurface | null {
    this.ray.ray.origin.copy(feet).addScaledVector(up, .35);
    this.ray.ray.direction.copy(up).negate();
    this.ray.near = 0;
    this.ray.far = .95;
    let surface: FootstepSurface | null = null;
    let nearest = this.ray.far;
    root.traverseVisible(object => {
      const single = object.userData.footstepSurface as FootstepSurface | undefined;
      const instances = object.userData.footstepSurfaces as FootstepSurface[] | undefined;
      if ((!single && !instances) || !(object instanceof THREE.Mesh)) return;
      object.updateWorldMatrix(true, false);
      this.hits.length = 0;
      this.ray.intersectObject(object, false, this.hits);
      for (const hit of this.hits) {
        if (hit.distance >= nearest || !hit.face) continue;
        this.matrix.copy(object.matrixWorld);
        if (object instanceof THREE.InstancedMesh && hit.instanceId !== undefined) {
          object.getMatrixAt(hit.instanceId, this.instance);
          this.matrix.multiply(this.instance);
        }
        this.normal.copy(hit.face.normal).applyNormalMatrix(this.normalMatrix.getNormalMatrix(this.matrix));
        if (this.normal.dot(up) < .45) continue; // wall sides are not flooring
        surface = instances?.[hit.instanceId ?? 0] ?? single ?? 'stone';
        nearest = hit.distance;
      }
    });
    return surface;
  }

  sampleAt(planet: { group: THREE.Group; def: PlanetDef; sampler: PlanetSampler }, feet: THREE.Vector3): FootstepSurface {
    planet.group.updateWorldMatrix(true, false);
    this.local.copy(feet);
    planet.group.worldToLocal(this.local);
    const radius = this.local.length();
    if (radius < 1) return 'stone';
    this.local.divideScalar(radius);
    this.up.copy(this.local).transformDirection(planet.group.matrixWorld);
    const floor = this.flooring(planet.group, feet, this.up);
    if (floor) return floor;
    planet.sampler.sample(this.local.x, this.local.y, this.local.z, this.sample, true);
    // Raised support (e.g. a climbable boulder or untagged structure) must not
    // inherit the grass/snow under it. Terrain itself is queried analytically.
    if (radius - planet.def.radius - this.sample.h * planet.sampler.amplitude > .45) return 'stone';
    planet.sampler.surfaceNormal(this.local.x, this.local.y, this.local.z, this.terrainNormal, 2);
    const slope = this.local.x * this.terrainNormal[0] + this.local.y * this.terrainNormal[1] + this.local.z * this.terrainNormal[2];
    return terrainFootstepSurface(planet.def, this.sample, planet.sampler.amplitude, slope);
  }
}
